import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DaySection from '../components/DailyNews/DaySection';
import Loading from '../components/Common/Loading';
import { PageErrorBoundary } from '../components/Common/ErrorBoundary';
import { DailySummaryData } from '../components/DailyNews/DailySummary';
import { Article } from '../config/api';
import { dailySummaryService } from '../services/dailySummaryService';
import { articleService } from '../services/articleService';
import './Home.css';

const DailySummaryDetail: React.FC = () => {
  const { dailyParam } = useParams<{ dailyParam: string }>();
  const navigate = useNavigate();
  const [dailySummary, setDailySummary] = useState<DailySummaryData | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDailySummary = async () => {
      if (!dailyParam) return;
      try {
        setLoading(true);
        const isNumeric = /^\d+$/.test(dailyParam);
        const response = isNumeric
          ? await dailySummaryService.getDailySummaryById(Number(dailyParam))
          : await dailySummaryService.getDailySummaryByDate(dailyParam);
        setDailySummary(response.dailySummary);
        if (response.articles && response.articles.length > 0) {
          setArticles(response.articles);
        } else {
          // Fallback to article service to ensure we capture latest data
          const articleResponse = await articleService.getArticles({
            dailySummaryIds: [response.dailySummary.id],
            limit: 1000,
            status: 'active',
          });
          setArticles(articleResponse.data || []);
        }
        setError(null);
      } catch (err) {
        console.error('Failed to load daily summary:', err);
        setError('notFound');
      } finally {
        setLoading(false);
      }
    };

    fetchDailySummary();
  }, [dailyParam]);

  if (loading) {
    return (
      <div className="home-container">
        <Loading type="skeleton-list" size="large" centered message="Loading daily summary..." />
      </div>
    );
  }

  if (error || !dailySummary) {
    return (
      <div className="home-container">
        <div className="error-state">
          <h3>Daily summary not found</h3>
          <p>The requested daily report could not be located.</p>
          <button onClick={() => navigate('/')} className="retry-button">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <PageErrorBoundary>
      <div className="home-container">
        <main className="home-content">
          <section className="home-section">
            <DaySection
              dailySummary={dailySummary}
              articles={articles}
              defaultExpanded
              showThemes
              showStats
              showArticleTags
            />
          </section>
        </main>
      </div>
    </PageErrorBoundary>
  );
};

export default DailySummaryDetail;
